// Budget the backend moves to / takes back from CCC and CC-12 when CC values change:
// - lowering a CC's depreciation value takes the difference back from CCC's budget (spInsertDepValue)
// - lowering its accrued-interest value takes it back from CC-12 (spInsertUpdateCCAccurate)
// - changing a performing CC's overhead % moves (new % - old %) of its approved DCA budget to / from CC-12
//   (spInsertOverheadValue), and a DCA budget amendment with a net reduction takes net x overhead % back from
//   CC-12 when approved (spApproveDCABudgetAmend); staging (spAmendDCABudget) and confirming
//   (spAmendMultipleDCABudget) already refuse it when CC-12's balance cannot cover that
// A take-back is refused with "Insufficient Budget Balance in <CC> ..." when that CC's Budget Balance does not cover it.

export const BUDGET_CC_LABELS = {
    CCC: 'Company Cost Center (CCC)',
    'CC-12': 'Raipur Office (CC-12)',
};

const currencyFmt = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 });
export const inr = (v) => currencyFmt.format(v);

// round to paise, halves away from zero like SQL ROUND(x, 2)
const round2 = (v) => (Math.sign(v) * Math.round(Math.abs(v) * 100)) / 100 || 0; // `|| 0` turns -0 into 0

/** Amount that will be taken back when a value goes from oldValue to newValue (0 when it is not lowered) */
export const amountToReturn = (oldValue, newValue) => {
    const oldNum = parseFloat(oldValue);
    const newNum = parseFloat(newValue);
    if (isNaN(oldNum) || isNaN(newNum) || newNum >= oldNum) return 0;
    return round2(oldNum - newNum);
};

/** CC-12's overhead share of a DCA budget amount: amount x percent / 100 (0 when either is not a number) */
export const overheadShare = (amount, percent) => {
    const a = parseFloat(amount);
    const p = parseFloat(percent);
    if (isNaN(a) || isNaN(p)) return 0;
    return round2((a * p) / 100);
};

/** CC-12 budget moved by an overhead % change: positive = credited to CC-12, negative = taken back */
export const overheadChange = (dcaBudget, oldPercent, newPercent) =>
    overheadShare(dcaBudget, (parseFloat(newPercent) || 0) - (parseFloat(oldPercent) || 0));

/** Net reduction of a DCA amendment batch (subtractions - additions), 0 when the net is not a reduction */
export const netReduction = (amendments) => {
    const net = (amendments || []).reduce((sum, a) => {
        const v = parseFloat(a.AmendedValue) || 0;
        return a.AmendmentType === 'Substract' ? sum + v : sum - v;
    }, 0);
    return net > 0 ? round2(net) : 0;
};
